/**
 * KYC Data Cleaner - Utility to clean up test/mock data that might be causing issues
 */

export const KYCDataCleaner = {
  /**
   * Clear all KYC-related data from localStorage
   */
  clearAllKYCData: () => {
    console.log('🧹 Clearing all KYC data...');
    
    const keysToRemove = [];
    
    // Get all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('kyc') || 
        key.includes('KYC') || 
        key.includes('merchant_kyc') ||
        key.includes('merchant_docs') ||
        key.includes('merchant_verification') ||
        key.includes('kyc_documents') ||
        key.includes('kyc_status')
      )) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all KYC-related keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed: ${key}`);
    });
    
    console.log(`✅ Cleared ${keysToRemove.length} KYC-related items`);
    
    return keysToRemove.length;
  },

  /**
   * Clear KYC data for a specific merchant
   */
  clearMerchantKYCData: (merchantId) => {
    console.log(`🧹 Clearing KYC data for merchant: ${merchantId}`);
    
    const keysToRemove = [
      'kycStatus',
      'kycVerificationComplete',
      `kyc_status_${merchantId}`,
      `merchant_kyc_${merchantId}`,
      `merchant_docs_${merchantId}`,
      `merchant_verification_${merchantId}`,
      `kyc_documents_${merchantId}`
    ];
    
    let removedCount = 0;
    keysToRemove.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`🗑️ Removed: ${key}`);
        removedCount++;
      }
    });
    
    console.log(`✅ Cleared ${removedCount} items for merchant ${merchantId}`);
    return removedCount;
  },

  /**
   * Reset all merchants to NOT_INITIATED status
   */
  resetAllMerchantsToNotInitiated: () => {
    console.log('🔄 Resetting all merchants to NOT_INITIATED status...');
    
    const keysToCheck = [];
    
    // Get all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('merchant_kyc_')) {
        keysToCheck.push(key);
      }
    }
    
    let resetCount = 0;
    keysToCheck.forEach(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (data && data.kycStatus) {
          data.kycStatus = {
            status: 'NOT_INITIATED',
            submittedAt: null,
            approvedAt: null,
            rejectedAt: null,
            rejectionReason: '',
            completionPercentage: 0,
            lastUpdated: new Date().toISOString()
          };
          
          localStorage.setItem(key, JSON.stringify(data));
          console.log(`🔄 Reset: ${key}`);
          resetCount++;
        }
      } catch (e) {
        console.warn(`Failed to reset ${key}:`, e);
      }
    });
    
    // Also clear general KYC status
    localStorage.removeItem('kycStatus');
    localStorage.removeItem('kycVerificationComplete');
    
    console.log(`✅ Reset ${resetCount} merchant profiles to NOT_INITIATED`);
    return resetCount;
  },

  /**
   * Check for problematic data that might cause issues
   */
  checkForProblematicData: () => {
    console.group('🔍 Checking for problematic KYC data...');
    
    const issues = [];
    
    // Check for general KYC status that might be interfering
    const generalStatus = localStorage.getItem('kycStatus');
    if (generalStatus) {
      try {
        const parsed = JSON.parse(generalStatus);
        if (parsed.status && parsed.status !== 'NOT_INITIATED') {
          issues.push({
            type: 'General KYC Status',
            key: 'kycStatus',
            issue: `Found general KYC status: ${parsed.status}`,
            data: parsed
          });
        }
      } catch (e) {
        issues.push({
          type: 'Parse Error',
          key: 'kycStatus',
          issue: 'Failed to parse general KYC status',
          data: generalStatus
        });
      }
    }
    
    // Check for completion flag
    const completionFlag = localStorage.getItem('kycVerificationComplete');
    if (completionFlag === 'true') {
      issues.push({
        type: 'Completion Flag',
        key: 'kycVerificationComplete',
        issue: 'Found completion flag set to true',
        data: completionFlag
      });
    }
    
    // Check merchant-specific data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('merchant_kyc_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data.kycStatus && data.kycStatus.status !== 'NOT_INITIATED') {
            issues.push({
              type: 'Merchant KYC Status',
              key: key,
              issue: `Merchant has status: ${data.kycStatus.status}`,
              data: data.kycStatus
            });
          }
        } catch (e) {
          issues.push({
            type: 'Parse Error',
            key: key,
            issue: 'Failed to parse merchant KYC data',
            data: localStorage.getItem(key)
          });
        }
      }
    }
    
    if (issues.length === 0) {
      console.log('✅ No problematic data found');
    } else {
      console.log(`⚠️ Found ${issues.length} potential issues:`);
      issues.forEach((issue, index) => {
        console.log(`${index + 1}. ${issue.type} (${issue.key}): ${issue.issue}`);
        console.log('   Data:', issue.data);
      });
    }
    
    console.groupEnd();
    return issues;
  },

  /**
   * Fix common KYC data issues
   */
  fixCommonIssues: () => {
    console.log('🔧 Fixing common KYC data issues...');
    
    let fixCount = 0;
    
    // Remove general completion flag
    if (localStorage.getItem('kycVerificationComplete')) {
      localStorage.removeItem('kycVerificationComplete');
      console.log('🔧 Removed general completion flag');
      fixCount++;
    }
    
    // Reset general KYC status if it's not NOT_INITIATED
    const generalStatus = localStorage.getItem('kycStatus');
    if (generalStatus) {
      try {
        const parsed = JSON.parse(generalStatus);
        if (parsed.status && parsed.status !== 'NOT_INITIATED') {
          localStorage.removeItem('kycStatus');
          console.log('🔧 Removed problematic general KYC status');
          fixCount++;
        }
      } catch (e) {
        localStorage.removeItem('kycStatus');
        console.log('🔧 Removed unparseable general KYC status');
        fixCount++;
      }
    }
    
    console.log(`✅ Applied ${fixCount} fixes`);
    return fixCount;
  }
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.KYCDataCleaner = KYCDataCleaner;
}

export default KYCDataCleaner;